/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Dropdown component
 *
 * @author Vladislav Oshkanov
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import React, {
    Children,
    cloneElement,
    createRef,
    Fragment,
    PureComponent,
} from 'react';
import type { ReactElement, KeyboardEvent, RefObject, ReactNode } from 'react';

import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { isMobile } from 'react-device-detect';

import { linkBlockClassName } from '@components/common/LinkBlock/LinkBlock';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import RenderInBody from '@modules/app/hoc/RenderInBody';
import withDefaultProps from '@modules/app/hoc/withDefaultProps';
import withWindowEvents, { IWindowEvent } from '@modules/app/hoc/WithWindowEvents';
import initTranslationDropdown from '@modules/internationalization/constants/common/dropdown';
import type ICommonTranslationDropdown from '@modules/internationalization/types/common/dropdown';

import Button from '../Button/Button';
import { LinkBlock } from '../index';
import { EInputKind } from '../Input/Input';
import type { IOptionItemProps } from '../Select/Select';
import DropDownFilterButtonCommon from './DropDownFilterButtonCommon';
import {
    ButtonAreaLeftStyled,
    ButtonAreaStyled,
    FilterButtonStyled,
    FilterContainerStyled,
    ToolTipStyled,
} from './styled/DropDownFilterStyled';

export enum DropDownRenderTypeEnum {
    input = 'input',
    select = 'select',
}

export enum DropDownButtonAreaTypeEnum {
    default = 'default',
    monthPicker = 'monthPicker',
}

export enum DropDownFilterButtonSize {
    small = 'small',
    medium = 'medium',
}

/**
 * @description  Props for IDropDownFilterProps component.
 * @interface IDropDownFilterProps
 *
 * @property{string} cancelButtonLabel Define text label for cancel button.
 * @property{string} clearButtonLabel Define text label for clear button.
 * @property{string} filterButtonLabel Define text label for filter button.
 * @property{IInputProps} input Define input props used as filter open button.
 * @property{string} kind Define styled due to theme kind.
 * @property{string} okButtonLabel Define text label for cancel button.
 * @property{string} onClearButtonClick Define text label for cancel button.
 * @property{string} onCloseModal Define text label for cancel button.
 * @property{string} onOkButtonClick Define text label for cancel button.
 * @property{boolean} showCancelButton Is cancel button show.
 * @property{boolean} showClearButton Is clear button show.
 * @property{boolean} showOkButton Is ok button show.
 */
export interface IDropDownFilterProps {
    onOpenAndClose?: (isOpen: boolean) => void;
    closeOnFilterButtonClick: boolean;
    dataName?: string;
    disabled?: boolean;
    filterButtonLabel?: string;
    filterButtonSize?: DropDownFilterButtonSize;
    icon: string;
    iconOpened?: string;
    kind: string;
    okButtonDisabled?: boolean;
    onCancelClick: () => void;
    onCloseClick: () => void;
    onOkClick: () => void;
    onSelect?: (item: IOptionItemProps) => void;
    placeholder?: string;
    render: () => ReactElement;
    renderButton?: () => ReactElement;
    renderButtonAreaType?: DropDownButtonAreaTypeEnum;
    renderInBody?: boolean;
    renderInBodyStyle?: string;
    renderType?: DropDownRenderTypeEnum;
    showCancelBtn?: boolean;
    showCloseButton?: boolean;
    translation: ICommonTranslationDropdown;
    widthAuto?: boolean;
    // пропс на случай если внутри выпадающего списка есть всплывающая подсказка со ссылкой
    // при клике по ссылке стандартная логика компонента НЕ должна срабатывать
    instructionLinkRef?: RefObject<HTMLAnchorElement>;
}

/**
 * @description DropDown state options
 * @interfaceIDropDownFilterState
 * @property {boolean} isToolTipShown - open Tooltip flag
 * @property {number | string} top - tooltip top position
 * @property {number | string} left - tooltip left position
 * @property {number | string} width - tooltip window width
 */
export interface IDropDownFilterState {
    componentLeft: number | null;
    isToolTipShown: boolean;
    top: number | string;
    left: number | string;
    width: number | string;
}

interface IRenderButtonProps {
    disabled?: boolean;
    filterButtonLabel?: string;
    icon: string;
    iconOpened?: string;
    isToolTipShown?: boolean;
    keyDownHandler?: (event: KeyboardEvent) => void;
    kind: string;
    placeholder?: string;
    selectDropRef?: RefObject<HTMLDivElement>;
    translation: ICommonTranslationDropdown;
}

export interface IStyledProps {
    kind: string;
}

const defaultProps: Partial<IDropDownFilterProps> = {
    closeOnFilterButtonClick: true,
    filterButtonLabel: '',
    kind: 'mediumNormal',
    okButtonDisabled: false,
    onCancelClick: undefined,
    onCloseClick: undefined,
    onOkClick: undefined,
    render: () => <ToolTipStyled className="drop-down-filter__tooltip" />,
    translation: initTranslationDropdown,
};
/**
 * @description the method checks whether the element has reached the scroll border or not
 * @param {HTMLDivElement} element - checked element
 * @param {number} deltaY - scroll event delta
 */
export const reachedBorder = (element: HTMLDivElement, deltaY: number): boolean => {
    if (!element) {
        return true;
    }
    const isElementBottom: boolean = element.scrollHeight - element.scrollTop === element.clientHeight && deltaY > 0;
    const isElementTop: boolean = element.scrollTop === 0 && deltaY < 0;

    return isElementBottom || isElementTop;
};

/**
 * @description Filter component containing a button, dropdown, which opens on button click.
 * @class DropDownFilter
 * @extends PureComponent<IDropDownFilterProps, IDropDownFilterState>
 */
export class DropDownFilter extends PureComponent<IDropDownFilterProps, IDropDownFilterState> {
    static defaultProps = defaultProps;

    state = {
        componentLeft: null,
        isToolTipShown: false,
        left: 0,
        top: 0,
        width: 0,
    };

    componentRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    selectDropRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    tooltipRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

    componentDidUpdate(prevProps: IDropDownFilterProps, prevState: IDropDownFilterState) {
        if ((!prevState.isToolTipShown && this.state.isToolTipShown) || prevState.top !== this.state.top) {
            this.correctTooltipLocation();
        }
        if (!prevState.isToolTipShown && this.state.isToolTipShown && this.props.onOpenAndClose) {
            this.props.onOpenAndClose(this.state.isToolTipShown);
        }
        if (prevState.isToolTipShown && !this.state.isToolTipShown && this.props.onOpenAndClose) {
            this.props.onOpenAndClose(this.state.isToolTipShown);
        }
    }

    /**
     * Check outside click, and close list if true
     * @param event
     */
    handleClickOutside = (event: MouseEvent): void => {

        if (
            this.props.instructionLinkRef?.current?.className !== linkBlockClassName &&
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            this.tooltipRef.current &&
            !this.tooltipRef.current.contains(event.target as Node) &&
            this.state.isToolTipShown
        ) {
            this.toggleToolTip();
            // handle close handler if exist
            get(this.props, 'onCloseClick');
        }
    };

    /**
     * Method apply select and close list, after enter press
     * @param event
     */
    windowKeyDownHandler = (event: KeyboardEvent): void => {
        if (
            event.keyCode === KeyboardKeyCodeEnum.ENTER &&
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            this.tooltipRef.current &&
            !this.tooltipRef.current.contains(event.target as Node) &&
            this.state.isToolTipShown
        ) {
            this.toggleToolTip();
            invoke(this.props, 'onOkClick');
        }
        if (
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            this.state.isToolTipShown
        ) {
            if (
                this.tooltipRef.current &&
                !this.tooltipRef.current.contains(event.target as Node) &&
                event.keyCode === KeyboardKeyCodeEnum.ENTER
            ) {
                this.toggleToolTip();
                invoke(this.props, 'onOkClick');
            } else if (
                this.tooltipRef.current &&
                this.tooltipRef.current.contains(event.target as Node) &&
                event.keyCode === KeyboardKeyCodeEnum.ESCAPE
            ) {
                this.toggleToolTip();
            }
        }
    };

    keyDownHandler = (event: KeyboardEvent): void => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER && !this.state.isToolTipShown) {
            event.stopPropagation();
            this.toggleToolTip();
        } else if (event.keyCode === KeyboardKeyCodeEnum.ESCAPE && this.state.isToolTipShown) {
            event.stopPropagation();
            this.toggleToolTip();
        }
    };

    closeTooltip = () => {
        this.setState({
            isToolTipShown: false,
        });
    };

    toggleToolTip = (): void => {
        this.setState(
            (state, props) => {
                const isToolTipShown = !state.isToolTipShown;
                let width: number | string = 0;
                let componentLeft: number | null = null;
                if (isToolTipShown && this.componentRef.current) {
                    width = props.widthAuto ? 'auto' : this.componentRef.current.offsetWidth;
                    componentLeft = this.componentRef.current.getBoundingClientRect().left;
                } else {
                    // call on close prop if dropdown closed
                    invoke(this.props, 'onCloseClick');
                }
                return {
                    componentLeft,
                    isToolTipShown,
                    width,
                };
            },
            () => {
                if (!this.state.isToolTipShown) {
                    invoke(this.selectDropRef, 'current.blur');
                }

                if (this.state.isToolTipShown && !isMobile) {
                    window.addEventListener('resize', this.toggleToolTip, true);
                } else {
                    window.removeEventListener('resize', this.toggleToolTip, true);
                }
            },
        );
    };

    handleFilterButtonClick = (): void => {
        if (!this.props.disabled && (!this.state.isToolTipShown || this.props.closeOnFilterButtonClick)) {
            this.toggleToolTip();
        }
    };

    handleOkButtonClick = (): void => {
        this.toggleToolTip();
        invoke(this.props, 'onOkClick');
    };

    handleCancelButtonClick = (): void => {
        this.toggleToolTip();
        invoke(this.props, 'onCancelClick');
    };

    handleCloseButtonClick = (): void => {
        this.toggleToolTip();
        invoke(this.props, 'onCloseClick');
    };

    /**
     * Method prevent scroll while wheel event if cursor inside filter
     */
    wheelHandler = (event: WheelEvent): void => {
        const tooltip = this.tooltipRef.current;
        if (
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            tooltip &&
            tooltip.contains(event.target as Node) &&
            this.state.isToolTipShown
        ) {
            const list = invoke(event, 'target.closest', '.list');
            if (reachedBorder(tooltip, event.deltaY) && reachedBorder(list, event.deltaY)) {
                event.stopPropagation();
                event.preventDefault();
            }
        }
    };

    /**
     * Method correct location if scroll event occurs
     */
    scrollHandler = (event: MouseEvent): void => {
        this.tooltipRef.current && !this.tooltipRef.current.contains(event.target as Node) && this.state.isToolTipShown
            ? this.correctTooltipLocation()
            : null;
    };

    /**
     * Method correct list coordinates according component location
     */
    correctTooltipLocation = (): void => {
        if (!this.tooltipRef.current || !this.componentRef.current) {
            return;
        }
        const { height: tooltipHeight, width: tooltipWidth } = this.tooltipRef.current.getBoundingClientRect();
        const {
            height: componentHeight,
            width: componentWidth,
            top: componentTop,
            left: componentLeft,
        } = this.componentRef.current.getBoundingClientRect();

        // Hide tooltip on horizontal scroll
        if (this.state.componentLeft !== null && componentLeft !== this.state.componentLeft && !isMobile) {
            this.toggleToolTip();
            return;
        }

        const left =
            componentLeft + tooltipWidth > window.innerWidth
                ? componentLeft + componentWidth - tooltipWidth
                : componentLeft;
        const top =
            componentTop + componentHeight + tooltipHeight < window.innerHeight
                ? componentTop + componentHeight
                : componentTop > tooltipHeight
                ? componentTop - tooltipHeight
                : 0;

        this.setState({
            top,
            left,
        });
    };

    getChildren = () => {
        return Children.map(this.props.children, (child: ReactNode) => {
            if (this.props.onSelect && child) {
                return cloneElement(child as ReactElement, {
                    selectItem: this.selectHandler,
                });
            } else {
                return child;
            }
        });
    };

    selectHandler = (item: IOptionItemProps): void => {
        this.toggleToolTip();
        if (this.props.onSelect && this.props.instructionLinkRef?.current?.className !== linkBlockClassName) {
            this.props.onSelect(item);
        }
    };

    render() {
        const {
            filterButtonLabel,
            icon,
            iconOpened,
            kind,
            onCancelClick,
            onCloseClick,
            onOkClick,
            render,
            renderInBody,
            renderType,
            showCancelBtn,
            showCloseButton,
            translation,
        } = this.props;

        const { isToolTipShown, top, left, width } = this.state;

        const dropDownClassName = isToolTipShown ? 'drop-down-filter_opened' : '';
        const container = !renderInBody && this.componentRef.current ? this.componentRef.current : undefined;
        const selectDropKind =
            kind === EInputKind.error ? EInputKind.error : filterButtonLabel ? EInputKind.filled : EInputKind.normal;

        const renderProps = {
            ref: this.tooltipRef,
            style: { top, left, width },
        };
        const renderButtonProps: IRenderButtonProps = {
            disabled: this.props.disabled,
            icon,
            iconOpened,
            kind: selectDropKind,
            placeholder: this.props.placeholder,
            translation,
        };
        if (renderType !== DropDownRenderTypeEnum.input) {
            renderButtonProps['filterButtonLabel'] = filterButtonLabel;
            renderButtonProps['isToolTipShown'] = isToolTipShown;
            renderButtonProps['keyDownHandler'] = this.keyDownHandler;
            renderButtonProps['selectDropRef'] = this.selectDropRef;
        }

        return (
            <FilterContainerStyled
                className={`drop-down-filter ${dropDownClassName}`}
                data-name={this.props.dataName}
                ref={this.componentRef}
            >
                <FilterButtonStyled
                    className="drop-down-filter__filter-button"
                    data-button="dropdown-filter"
                    data-name="dropdown-filter"
                    filterButtonSize={this.props.filterButtonSize}
                    kind={this.props.kind}
                    onClick={this.handleFilterButtonClick}
                >
                    {cloneElement(
                        this.props.renderButton ? (
                            this.props.renderButton()
                        ) : (
                            <DropDownFilterButtonCommon filterButtonSize={this.props.filterButtonSize} />
                        ),
                        { ...renderButtonProps },
                    )}
                </FilterButtonStyled>
                {isToolTipShown && (
                    <RenderInBody modalRoot={container}>
                        {cloneElement(
                            render(),
                            renderProps,
                            <Fragment>
                                <div className="drop-down-filter__content" data-name="drop-down-filter-content">
                                    {this.getChildren()}
                                </div>
                                {(onOkClick || onCancelClick || onCloseClick) && (
                                    <ButtonAreaStyled renderType={this.props.renderButtonAreaType}>
                                        <ButtonAreaLeftStyled>
                                            {
                                                // @ts-ignore
                                                onOkClick && (
                                                    <Button
                                                        className="drop-down-filter__ok-button"
                                                        dataName="ok"
                                                        disabled={this.props.okButtonDisabled}
                                                        kind="medium"
                                                        onClick={this.handleOkButtonClick}
                                                    >
                                                        {translation.okBtn}
                                                    </Button>
                                                )
                                            }
                                            {showCancelBtn && (
                                                <LinkBlock
                                                    className="drop-down-filter__cancel-button"
                                                    dataName="cancel"
                                                    onClick={this.handleCancelButtonClick}
                                                >
                                                    {translation.cancelBtn}
                                                </LinkBlock>
                                            )}
                                        </ButtonAreaLeftStyled>
                                        {showCloseButton && (
                                            <LinkBlock
                                                className="drop-down-filter__clear-button"
                                                dataName="clear"
                                                onClick={this.handleCloseButtonClick}
                                            >
                                                {translation.closeBtn}
                                            </LinkBlock>
                                        )}
                                    </ButtonAreaStyled>
                                )}
                            </Fragment>,
                        )}
                    </RenderInBody>
                )}
            </FilterContainerStyled>
        );
    }
}

const dropFilterEvents: IWindowEvent[] = [
    {
        event: 'mousedown',
        handler: 'handleClickOutside',
    },
    {
        event: 'keydown',
        handler: 'windowKeyDownHandler',
    },
    {
        event: 'scroll',
        handler: 'scrollHandler',
    },
    {
        event: 'wheel',
        handler: 'wheelHandler',
        options: {
            passive: false,
        },
    },
];

export default withWindowEvents(withDefaultProps<IDropDownFilterProps>(defaultProps, DropDownFilter), dropFilterEvents);
